import { useMemo } from 'react';
import { useAcademicYears, useTerms } from '@/hooks/useAdminQueries';
import { useAcademicYearStore } from '@/store/academicYearStore';
import { termsForYear } from '@/lib/academicYearTerms';
import type { AcademicYear, Term } from '@/types/api';

export interface SelectedAcademicYear {
  years: AcademicYear[];
  /** Every term the school has, across all years. */
  allTerms: Term[];
  /** The year in scope: the stored choice, else the school's active year. */
  selectedYear: AcademicYear | null;
  /** The school's live year, whatever is currently being looked at. */
  activeYear: AcademicYear | null;
  /** Terms of the year in scope, in teaching order. */
  selectedTerms: Term[];
  /** How many terms each year has, keyed by year id. */
  termCounts: Record<string, number>;
  isLoading: boolean;
  selectYear: (id: string) => void;
  /** True when looking at a year other than the school's active one. */
  isOutOfActiveYear: boolean;
}

/**
 * The app-wide academic year, resolved.
 *
 * The stored id is deliberately not trusted: it is looked up in the list the
 * API actually returned, so a year from another school (this is a multi-school
 * app and the choice is persisted per browser, not per school) or one that has
 * been deleted quietly falls back to the active year instead of scoping the app
 * to nothing.
 *
 * Terms are fetched unfiltered and grouped client-side because the popover
 * needs both the selected year's full term detail *and* a term count for every
 * other year — one request rather than one per year.
 *
 * Both endpoints are teacher-or-admin only, so this must only be mounted behind
 * that check; there is no `enabled` flag because the caller not rendering is
 * the gate.
 */
export function useSelectedAcademicYear(): SelectedAcademicYear {
  const { data: years = [], isLoading: yearsLoading } = useAcademicYears();
  const { data: terms = [], isLoading: termsLoading } = useTerms();

  const selectedYearId = useAcademicYearStore((state) => state.selectedYearId);
  const selectYear = useAcademicYearStore((state) => state.setSelectedYearId);

  const activeYear = years.find((year) => year.isActive) ?? null;
  const stored = selectedYearId
    ? (years.find((year) => year.id === selectedYearId) ?? null)
    : null;
  const selectedYear = stored ?? activeYear ?? years[0] ?? null;

  // This hook lives on the shell's topbar, so it re-runs on every route change
  // and every unrelated store update. Grouping is a filter-and-sort of the
  // whole term list per year, which has no business being redone unless one of
  // the two lists actually changed.
  const termCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const year of years) {
      counts[year.id] = termsForYear(terms, year).length;
    }
    return counts;
  }, [years, terms]);

  const selectedTerms = useMemo(
    () => (selectedYear ? termsForYear(terms, selectedYear) : []),
    [terms, selectedYear]
  );

  return {
    years,
    allTerms: terms,
    selectedYear,
    activeYear,
    selectedTerms,
    termCounts,
    isLoading: yearsLoading || termsLoading,
    selectYear,
    isOutOfActiveYear: Boolean(selectedYear && activeYear && selectedYear.id !== activeYear.id),
  };
}
